// Help > Report a problem: the text the user sends to support. Kept apart
// from the dialog so the exact wording can be tested. The details the user
// would never know to include (version, page, project, last simulation,
// browser) are added automatically, so a report can be reproduced.
import { formatGreekDateTime } from "./announcements";

export const REPORT_KINDS = ["problem", "suggestion", "question"];

const KIND = {
  problem: ["Πρόβλημα", "Problem"],
  suggestion: ["Πρόταση", "Suggestion"],
  question: ["Ερώτηση", "Question"],
};

const RUN_STATUS = {
  completed: ["Ολοκληρώθηκε", "Completed"],
  success: ["Ολοκληρώθηκε", "Completed"],
  failed: ["Απέτυχε", "Failed"],
  running: ["Εκτελείται", "Running"],
  pending: ["Σε αναμονή", "Queued"],
  queued: ["Σε αναμονή", "Queued"],
  cancelled: ["Ακυρώθηκε", "Cancelled"],
  not_started: ["Δεν έχει τρέξει", "Not run yet"],
};

const pick = (pair, isGreek) => (pair ? pair[isGreek ? 0 : 1] : "");

export function runStatusText(run, isGreek) {
  if (!run || !run.status) return null;
  const parts = [pick(RUN_STATUS[run.status], isGreek) || String(run.status)];
  if (run.last_run_at) parts.push(formatGreekDateTime(run.last_run_at));
  if (run.feasibility_id) parts.push(`ID ${run.feasibility_id}`);
  let text = parts.join(" · ");
  if (run.error_message) text += `\n  ${isGreek ? "Μήνυμα" : "Message"}: ${run.error_message}`;
  return text;
}

/** { subject, details, body } in the screen's language; body = description + details. */
export function buildProblemReport({ kind = "problem", description = "", context = {}, run = null, isGreek = true, now = new Date() }) {
  const project = context.projectName
    ? `${context.projectName}${context.projectId ? ` (ID ${context.projectId})` : ""}`
    : (isGreek ? "κανένα ανοιχτό έργο" : "no project open");
  const kindText = pick(KIND[kind] || KIND.problem, isGreek);
  const subject = `${context.appName || "PYTHIA"} – ${kindText}${context.projectName ? `: ${context.projectName}` : ""}`;
  const lines = [
    `${isGreek ? "Είδος" : "Type"}: ${kindText}`,
    `${isGreek ? "Πλατφόρμα" : "Platform"}: ${context.appName || "PYTHIA"} ${context.appVersion || ""}`.trim(),
    `${isGreek ? "Ημερομηνία" : "Date"}: ${formatGreekDateTime(now)} ${isGreek ? "(ώρα Ελλάδος)" : "(Greek time)"}`,
  ];
  if (context.userEmail) lines.push(`${isGreek ? "Λογαριασμός" : "Account"}: ${context.userEmail}`);
  lines.push(`${isGreek ? "Σελίδα" : "Page"}: ${context.page || "-"}`);
  lines.push(`${isGreek ? "Έργο" : "Project"}: ${project}`);
  const runText = runStatusText(run, isGreek);
  if (runText) lines.push(`${isGreek ? "Τελευταία προσομοίωση" : "Last simulation"}: ${runText}`);
  lines.push(`${isGreek ? "Γλώσσα" : "Language"}: ${isGreek ? "Ελληνικά" : "English"}`);
  if (context.screen) lines.push(`${isGreek ? "Οθόνη" : "Screen"}: ${context.screen}`);
  if (context.browser) lines.push(`Browser: ${context.browser}`);
  const details = lines.join("\n");
  const header = isGreek ? "— Στοιχεία που προστέθηκαν αυτόματα —" : "— Details added automatically —";
  return { subject, details, body: `${(description || "").trim()}\n\n${header}\n${details}` };
}

const enc = encodeURIComponent;

export function mailtoUrl(to, { subject, body }) {
  return `mailto:${to}?subject=${enc(subject)}&body=${enc(body)}`;
}

export function gmailUrl(to, { subject, body }) {
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${enc(to)}&su=${enc(subject)}&body=${enc(body)}`;
}

export function outlookUrl(to, { subject, body }) {
  return `https://outlook.office.com/mail/deeplink/compose?to=${enc(to)}&subject=${enc(subject)}&body=${enc(body)}`;
}
